import { memo } from 'react';
import { Form } from 'antd';
import { cn } from '@/libs/utils';
import { EMPLOYEE_GENERAL_DATA_FORM } from '@/shared/employee';
import EmployeeGeneralDataFormContext from './employee-general-data/EmployeeGeneralDataFormContext';
import EmployeeAllowancesFields from './employee-general-data/sections/EmployeeAllowancesFields';
import EmployeeContractFields from './employee-general-data/sections/EmployeeContractFields';
import EmployeeContractProfilePanel from './employee-general-data/sections/EmployeeContractProfilePanel';
import EmployeeDocumentsFields from './employee-general-data/sections/EmployeeDocumentsFields';
import EmployeeFormFooter from './employee-general-data/sections/EmployeeFormFooter';
import EmployeeHolidayDaysFields from './employee-general-data/sections/EmployeeHolidayDaysFields';
import EmployeeHolidayProfilePanel from './employee-general-data/sections/EmployeeHolidayProfilePanel';
import EmployeeHolidaySummary from './employee-general-data/sections/EmployeeHolidaySummary';
import EmployeeOrgProfilePanel from './employee-general-data/sections/EmployeeOrgProfilePanel';
import EmployeePaymentProfilePanel from './employee-general-data/sections/EmployeePaymentProfilePanel';
import EmployeePersonalFields from './employee-general-data/sections/EmployeePersonalFields';
import EmployeePersonalProfilePanel from './employee-general-data/sections/EmployeePersonalProfilePanel';
import EmployeeRemarksFields from './employee-general-data/sections/EmployeeRemarksFields';
import EmployeeWorkTimeFields from './employee-general-data/sections/EmployeeWorkTimeFields';
import useEmployeeCompanyData from './employee-general-data/useEmployeeCompanyData';
import useEmployeeEmploymentFields from './employee-general-data/useEmployeeEmploymentFields';
import useEmployeeFormActions from './employee-general-data/useEmployeeFormActions';
import useEmployeeFormBase from './employee-general-data/useEmployeeFormBase';
import useEmployeeGeneralFields from './employee-general-data/useEmployeeGeneralFields';

function EmployeeGeneralDataForm({
  formData,
  isEdited,
  className,
  employeeId,
  onAdditionalInfoDirtyChange,
  // Profile page layout: the form stays one mounted form (so Save sends exactly
  // what it always sent) but shows only the fields of the selected tile.
  // Fields of other tiles are hidden, never unmounted.
  profileSection = null,
  readOnly = false,
  form: formProp,
  onSaved,
  onCancel,
  onFinishFailed,
  // The employee exactly as the server returned it (profile page only): a
  // changed group is sent as stored value + form values, so keys the form
  // does not render are kept.
  serverData,
  // Profile page: called with the tiles whose fields differ from the loaded
  // values, for the unsaved-changes marks.
  onDirtySectionsChange,
  // Profile page: receives { save, discard } of the open tile.
  actionsRef,
}) {
  const props = {
    actionsRef,
    className,
    employeeId,
    formData,
    formProp,
    isEdited,
    onAdditionalInfoDirtyChange,
    onCancel,
    onDirtySectionsChange,
    onFinishFailed,
    onSaved,
    profileSection,
    readOnly,
    serverData,
  };
  const employeeFormBase = useEmployeeFormBase({ ...props });
  const employeeGeneralFields = useEmployeeGeneralFields({
    ...props,
    ...employeeFormBase,
  });
  const employeeEmploymentFields = useEmployeeEmploymentFields({
    ...props,
    ...employeeFormBase,
    ...employeeGeneralFields,
  });
  const employeeCompanyData = useEmployeeCompanyData({
    ...props,
    ...employeeFormBase,
    ...employeeGeneralFields,
    ...employeeEmploymentFields,
  });
  const employeeFormActions = useEmployeeFormActions({
    ...props,
    ...employeeFormBase,
    ...employeeGeneralFields,
    ...employeeEmploymentFields,
    ...employeeCompanyData,
  });
  const sectionContext = {
    ...props,
    ...employeeFormBase,
    ...employeeGeneralFields,
    ...employeeEmploymentFields,
    ...employeeCompanyData,
    ...employeeFormActions,
  };
  const {
    form,
    isHolidayTrackerCompany,
    isNonEmployment,
    isProfile,
    onFinish,
    sectionClass,
  } = sectionContext;

  return (
    <EmployeeGeneralDataFormContext.Provider value={sectionContext}>
      <Form
        form={form}
        name="employee_form"
        initialValues={{
          ...EMPLOYEE_GENERAL_DATA_FORM,
          ...formData,
        }}
        layout="vertical"
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        autoComplete="off"
        disabled={readOnly}
        className={cn(
          'form p-4',
          isProfile && 'employee-profile-form',
          readOnly && 'employee-profile-form--readonly',
          className
        )}
      >
        {isProfile && <EmployeePersonalProfilePanel />}

        <EmployeePersonalFields />

        {isProfile && <EmployeePaymentProfilePanel />}

        {isProfile && <EmployeeOrgProfilePanel />}

        {isProfile && <EmployeeContractProfilePanel />}

        <EmployeeContractFields />

        <EmployeeWorkTimeFields />

        {/* Annual-leave / holiday tracker card. Special employment has no annual
          leave → hidden for those types. */}
        <div
          className={cn(
            !isProfile && 'rounded border bg-zinc-50 p-4',
            isHolidayTrackerCompany && !isNonEmployment ? 'block' : 'hidden',
            sectionClass('go')
          )}
        >
          {isProfile && <EmployeeHolidayProfilePanel />}

          <EmployeeHolidayDaysFields />

          <EmployeeHolidaySummary />
        </div>

        <EmployeeAllowancesFields />

        <EmployeeDocumentsFields />

        <EmployeeRemarksFields />

        <EmployeeFormFooter />
      </Form>
    </EmployeeGeneralDataFormContext.Provider>
  );
}

export default memo(EmployeeGeneralDataForm);
